import React, { useRef } from 'react';

import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';

import { marketManipulationStatementPart1, marketManipulationStatementPart2 } from 'constants/statements';
import { Icon } from 'DesignSystem2/Icon';
import { Popover, PopoverHandle } from 'DesignSystem2/Popover';
import { Row, RowGroup } from 'DesignSystem2/Row';
import Text from 'DesignSystem2/Text';
import { Spacing } from 'DesignSystem2/Theme';
import Palette from 'DesignSystem2/Theme/Palette';
import config from 'utils/config';

const useStyles = createUseStyles({
    rowGroup: {
        display: 'flex',
        width: 195,
        padding: `${Spacing(1)} ${Spacing(2)}`,
    },
    row: {
        paddingTop: Spacing(1),
        paddingBottom: Spacing(1),
        paddingLeft: Spacing(2),
        paddingRight: Spacing(2),
    },
});

interface SignUpComplianceAgreeProps {
    customText?: string;
    customEmphasis?: string;
    size?: 'small' | 'medium' | 'large';
    center?: boolean;
}

const SignUpComplianceAgree = ({
    customText,
    customEmphasis,
    size = 'medium',
    center,
}: SignUpComplianceAgreeProps): JSX.Element => {
    const classes = useStyles();
    const popoverRef = useRef<PopoverHandle>(null);

    const onOpen = () => {
        if (!!popoverRef.current) {
            popoverRef.current.open();
        }
    };

    const onClose = () => {
        if (!!popoverRef.current) {
            popoverRef.current.close();
        }
    };

    const memberAgreementUrl = '/docs/kalshi-member-agreement.pdf';
    const rulebookUrl = `/regulatory/rulebook`;
    const privacyPolicyUrl = '/privacy-policy';
    const sourceAgencyUrl = `${config.kalshiPublicDocsCloudfrontUrl}/kalshi-source-agency-trading-prohibitions.pdf`;
    const klearUrl = `${config.kalshiPublicDocsS3BucketUrl}/regulatory-klear/terms/klear-tos.pdf`;
    const klearRulebookUrl = `${config.kalshiPublicDocsS3BucketUrl}/regulatory-klear/rulebook/klear_rulebook_1.00.pdf`;

    const TextComponent = size === 'small' ? Text.Body.x10 : Text.Body.x20;
    const EmphasisComponent = size === 'small' ? Text.Emphasis.x10 : Text.Emphasis.x20;

    return (
        <Popover
            popoverHandleRef={popoverRef}
            content={
                <div className={classes.rowGroup}>
                    <RowGroup
                        style={{ width: '100%' }}
                        rows={[
                            <Row
                                key={'member-agreement-row'}
                                labelText={<Text.Emphasis.x10>{'Member Agreement'}</Text.Emphasis.x10>}
                                className={classes.row}
                                externalHref={memberAgreementUrl}
                                onClick={onClose}
                            />,
                            <Row
                                key={'rulebook-row'}
                                labelText={<Text.Emphasis.x10>{'Rulebook'}</Text.Emphasis.x10>}
                                className={classes.row}
                                externalHref={rulebookUrl}
                                onClick={onClose}
                            />,
                            <Row
                                key={'klear-row'}
                                labelText={<Text.Emphasis.x10>{'Kalshi Klear Rulebook'}</Text.Emphasis.x10>}
                                className={classes.row}
                                externalHref={klearRulebookUrl}
                                onClick={onClose}
                            />,
                            <Row
                                key={'privacy-policy-row'}
                                labelText={<Text.Emphasis.x10>{'Privacy Policy'}</Text.Emphasis.x10>}
                                className={classes.row}
                                externalHref={privacyPolicyUrl}
                                onClick={onClose}
                            />,
                            <Row
                                key={'source-agency-row'}
                                labelText={<Text.Emphasis.x10>{'Trading Prohibition'}</Text.Emphasis.x10>}
                                className={classes.row}
                                externalHref={sourceAgencyUrl}
                                onClick={onClose}
                            />,
                            <Row
                                key={'klear-row'}
                                labelText={
                                    <Text.Emphasis.x10>{'Kalshi Klear Participant Agreement'}</Text.Emphasis.x10>
                                }
                                className={classes.row}
                                externalHref={klearUrl}
                                onClick={onClose}
                            />,
                        ]}
                    />
                </div>
            }
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    textAlign: center ? 'center' : undefined,
                    cursor: 'pointer',
                }}
                onClick={onOpen}
            >
                <div>
                    <TextComponent color={Palette.Text.x20}>
                        {customText || marketManipulationStatementPart1}
                    </TextComponent>
                    <EmphasisComponent color={Palette.Text.x20}>{` ${
                        customEmphasis || marketManipulationStatementPart2
                    } `}</EmphasisComponent>
                    <Icon.ArrowForward color={Palette.Text.x20} size={'10px'} />
                </div>
            </div>
        </Popover>
    );
};

export default observer(SignUpComplianceAgree);
